/**
 * Plattform-Limits („Nitro frei, Admin setzt Grenzen"). Gespeichert in `PlatformSetting` (Key-Value,
 * key = Name unten, value = JSON). Fehlende Schlüssel → diese Standardwerte. 0 bedeutet „unbegrenzt"
 * bei allen `max*`-Zahlen (außer maxUploadBytes/maxMessageLength, die immer > 0 sind).
 */
export const SCREENSHARE_PRESETS = ['720p30', '1080p60', '1440p60', '2160p30'] as const;
export type ScreensharePreset = (typeof SCREENSHARE_PRESETS)[number];

export const SCREENSHARE_PRESET_SPECS: Record<ScreensharePreset, { width: number; height: number; fps: number; maxBitrate: number }> = {
  '720p30': { width: 1280, height: 720, fps: 30, maxBitrate: 2_500_000 },
  '1080p60': { width: 1920, height: 1080, fps: 60, maxBitrate: 6_000_000 },
  '1440p60': { width: 2560, height: 1440, fps: 60, maxBitrate: 10_000_000 },
  '2160p30': { width: 3840, height: 2160, fps: 30, maxBitrate: 15_000_000 },
};

export interface PlatformLimits {
  /** Max. Größe je Datei in Bytes (Standard 2 GiB). */
  maxUploadBytes: number;
  /** Max. Zeichen je Nachricht. */
  maxMessageLength: number;
  maxAttachmentsPerMessage: number;
  maxEmojisPerServer: number;
  maxStickersPerServer: number;
  maxSoundsPerServer: number;
  /** Max. Server, die ein Nutzer besitzen/beitreten kann (0 = unbegrenzt). */
  maxServersPerUser: number;
  maxGroupDmParticipants: number;
  maxPinsPerChannel: number;
  maxReactionsPerMessage: number;
  /** Max. Größe von Emoji/Sticker/Sound-Dateien in Bytes. */
  maxEmojiBytes: number;
  maxStickerBytes: number;
  maxSoundBytes: number;
  maxSoundDurationMs: number;
  /** Max. Avatar/Banner/Server-Icon in Bytes (GIF/WebP animiert erlaubt). */
  maxAvatarBytes: number;
  screenshareMaxPreset: ScreensharePreset;
  /** Max. Audio-Bitrate in bit/s (Kanal-Bitrate wird darauf gedeckelt). */
  maxAudioBitrate: number;
  /** Max. Teilnehmer je Sprachkanal (0 = unbegrenzt; Kanal-userLimit darf kleiner sein). */
  maxVoiceUsers: number;
  registrationOpen: boolean;
  /** Offene geplante Nachrichten je Nutzer. */
  scheduledMessageMax: number;
  remindersMax: number;
  /** Super-Reaktionen je Nutzer und Tag (0 = unbegrenzt). */
  superReactionsPerDay: number;
  maxCustomCssBytes: number;
  maxBioLength: number;
  /** Aufbewahrung fertiger Exporte in Stunden. */
  exportRetentionHours: number;
  /** Nicht an Nachrichten gebundene Uploads nach X Stunden löschen. */
  orphanUploadHours: number;
  maxApiTokensPerUser: number;
  maxBotsPerUser: number;
  maxWebhooksPerChannel: number;
}

export const DEFAULT_LIMITS: PlatformLimits = {
  maxUploadBytes: 2 * 1024 * 1024 * 1024,
  maxMessageLength: 8000,
  maxAttachmentsPerMessage: 10,
  maxEmojisPerServer: 0,
  maxStickersPerServer: 0,
  maxSoundsPerServer: 0,
  maxServersPerUser: 0,
  maxGroupDmParticipants: 25,
  maxPinsPerChannel: 0,
  maxReactionsPerMessage: 0,
  maxEmojiBytes: 2 * 1024 * 1024,
  maxStickerBytes: 2 * 1024 * 1024,
  maxSoundBytes: 2 * 1024 * 1024,
  maxSoundDurationMs: 10_000,
  maxAvatarBytes: 20 * 1024 * 1024,
  screenshareMaxPreset: '1440p60',
  maxAudioBitrate: 384_000,
  maxVoiceUsers: 0,
  registrationOpen: true,
  scheduledMessageMax: 100,
  remindersMax: 500,
  superReactionsPerDay: 0,
  maxCustomCssBytes: 200 * 1024,
  maxBioLength: 2000,
  exportRetentionHours: 72,
  orphanUploadHours: 24,
  maxApiTokensPerUser: 25,
  maxBotsPerUser: 10,
  maxWebhooksPerChannel: 15,
};

export type PlatformLimitKey = keyof PlatformLimits;

/** Zusammenführen gespeicherter Werte mit den Standards (unbekannte Schlüssel werden ignoriert). */
export function mergeLimits(stored: Partial<Record<string, unknown>>): PlatformLimits {
  const out: PlatformLimits = { ...DEFAULT_LIMITS };
  for (const key of Object.keys(DEFAULT_LIMITS) as PlatformLimitKey[]) {
    const v = stored[key];
    if (v === undefined || v === null) continue;
    if (typeof v === typeof DEFAULT_LIMITS[key]) (out as unknown as Record<string, unknown>)[key] = v;
  }
  return out;
}

/** 0 = unbegrenzt. */
export function withinLimit(current: number, max: number): boolean {
  return max === 0 || current < max;
}

/** Sonstige feste Grenzen (nicht vom Admin einstellbar). */
export const FIXED_LIMITS = {
  usernameMin: 2,
  usernameMax: 32,
  displayNameMax: 32,
  passwordMin: 8,
  passwordMax: 256,
  serverNameMax: 100,
  channelNameMax: 100,
  channelTopicMax: 1024,
  roleNameMax: 100,
  nicknameMax: 32,
  pronounsMax: 40,
  customStatusMax: 128,
  emojiNameMin: 2,
  emojiNameMax: 32,
  forumTagsMax: 20,
  threadTitleMax: 100,
  pageSizeDefault: 50,
  pageSizeMax: 100,
  searchPageSize: 25,
  bookmarkNoteMax: 500,
  reminderNoteMax: 500,
  webhookNameMax: 80,
} as const;
